import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useLocation, useParams } from 'react-router-dom';
import { getPopular } from './PopularItems';
import { cars } from '../constants';


/**
 * Custom hook for filtering, sorting, and paginating cars.
 */
export const useCarFilter = () => {

  const location = useLocation(); // Get the current route
  const navigate = useNavigate();
  const { search } = useParams(); // Get search keyword from URL params
  const [searchParams] = useSearchParams(); // Get URL search params

  // State variables
  const [matchingCars, setMatchingCars] = useState([]); // Filtered and sorted cars
  const [currentPage, setCurrentPage] = useState(1); // Current page for pagination
  const [sortOption, setSortOption] = useState(''); // Current sorting option
  const [searchKeyword, setSearchKeyword] = useState(''); // Search keyword for header search

  // Set items per page based on route (Grid or List page)
  const itemsPerPage = location.pathname.toLowerCase().includes('list') ? 4 : 6;

  // Synchronize search keyword with URL params
  useEffect(() => {
    setSearchKeyword(search || '');
  }, [search]);

  // Filter and sort cars whenever search params, keyword, or sort option changes
  useEffect(() => {

    // Extract search parameters from the URL
    const carMake = searchParams.get('carMake') || '';
    const carModel = searchParams.get('carModel') || '';
    const carType = searchParams.get('carType') || '';
    const carYear = searchParams.get('carYear') || '';
    const carCondition = searchParams.get('carCondition') || '';
    const carFuelType = searchParams.get('carFuelType') || '';
    const maxPrice = searchParams.get('maxPrice') || '';

    // Filter cars based on search criteria
    let filteredCars = handleSearch(
      carMake,
      carModel,
      carType,
      carYear,
      carCondition,
      carFuelType,
      maxPrice,
      cars
    );

    // Apply header search keyword filter
    if (searchKeyword) {
      filteredCars = handleHeaderSearch(searchKeyword);
    }

    // Sort filtered cars
    filteredCars = handleSortCars(filteredCars, sortOption);

    // Update matching cars and reset to the first page
    setMatchingCars(filteredCars);
    setCurrentPage(1);  

  }, [searchParams, searchKeyword, sortOption]);

  // Handles sorting of cars based on the selected option.
  const handleSortCars = (cars, sortOption) => {
    switch (sortOption) {
      case 'featured':
        return getPopular(cars);
      case 'latest':
        return [...cars].sort((a, b) => b.details.year - a.details.year);
      case 'lowPrice':
        return [...cars].sort((a, b) => a.price - b.price);
      case 'highPrice':
        return [...cars].sort((a, b) => b.price - a.price);
      default:
        return cars;
    }
  };

  // Function to filter cars based on search criteria
  const handleSearch = (
    carMake,
    carModel,
    carType,
    carYear,
    carCondition,
    carFuelType,
    maxPrice,
    cars
  ) => {
    return cars.filter((car) => {

      const parsedMaxPrice = maxPrice ? Number(maxPrice) : Infinity;
      const parsedCarYear = carYear ? parseInt(carYear, 10) : null;

      return (
        (carMake === '' || car.details.carMake.toLowerCase().includes(carMake.toLowerCase())) &&
        (carModel === '' || car.details.carModel.toLowerCase().includes(carModel.toLowerCase())) &&
        (carType === '' || car.details.carType.toLowerCase().includes(carType.toLowerCase())) &&
        (!parsedCarYear || car.details.year === parsedCarYear) &&
        (carCondition === '' || car.condition.toLowerCase() === carCondition.toLowerCase()) &&
        (carFuelType === '' || car.details.fuelType.toLowerCase() === carFuelType.toLowerCase()) &&
        car.price <= parsedMaxPrice
      );
    });
  };

  // Filters cars based on a search keyword (header search).
  const handleHeaderSearch = (searchTerm) => {

    const searchLower = searchTerm.toLowerCase();
    return cars.filter((item) => (
      String(item.details.year).toLowerCase().includes(searchLower) || 
      item.details.carMake?.toLowerCase().includes(searchLower) ||
      item.details.carModel?.toLowerCase().includes(searchLower) ||
      item.details.carType?.toLowerCase().includes(searchLower) ||
      item.details.fuelType?.toLowerCase().includes(searchLower) ||
      item.details.transmission?.toLowerCase().includes(searchLower) ||
      item.condition?.toLowerCase().includes(searchLower)
    ));
  
  };

  /* ==========================
    FORM SUBMISSION HANDLERS
  ========================== */

  // Handles form submission and updates the URL with search parameters.
  const handleFormSubmit = (
    carMake,
    carModel,
    carType,
    carYear,
    carCondition,
    carFuelType,
    maxPrice
  ) => {
    const params = new URLSearchParams();
    if (carMake) params.set('carMake', carMake);
    if (carModel) params.set('carModel', carModel);
    if (carType) params.set('carType', carType);
    if (carYear) params.set('carYear', carYear);
    if (carCondition) params.set('carCondition', carCondition);
    if (carFuelType) params.set('carFuelType', carFuelType);
    if (maxPrice) params.set('maxPrice', maxPrice);

    navigate(`/Cars?${params.toString()}`);
    setCurrentPage(1); // Reset to the first page on new search
  };

  // Handles header search form submission and updates the URL.
  const handleHeaderSearchSubmit = (selectedSearchQuery, type) => {
    const encodedQuery = encodeURIComponent(selectedSearchQuery);
    navigate(`/Cars/search/${encodedQuery}`);
  };

  // Function to handle sorting option change
  const handleSort = (selectedOption) => {
    setSortOption(selectedOption ? selectedOption.value : '');
    setCurrentPage(1); // Reset to the first page on sort change
  };

  // Pagination calculations
  const displayCars = matchingCars.length > 0 ? matchingCars : cars;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedCars = displayCars.slice(startIndex, endIndex);
  const totalPages = Math.ceil(displayCars.length / itemsPerPage);

  // Function to handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return {
    searchKeyword,
    matchingCars,
    sortOption,
    currentPage,
    displayedCars,
    handleFormSubmit,
    handleSort,
    totalPages,
    handlePageChange,
    handleHeaderSearchSubmit
  };

};





// ================================
// Extract Dynamic Counts for Filtering
// ================================

const carMakeCounts = {}; // Stores count of each car make
const carModelsByMake = {}; // Stores car models grouped by make
const carTypeCounts = {}; // Stores count of each car type
const carFuelCounts = {}; // Stores count of each fuel type
const carConditionCounts = {}; // Stores count of each car condition

// Iterate through the cars array to populate counts
cars.forEach((car) => {

  const make = car.details.carMake.toLowerCase();
  const model = car.details.carModel.toLowerCase();
  const type = car.details.carType.toLowerCase();
  const fuel = car.details.fuelType.toLowerCase();
  const condition = car.condition.toLowerCase();

  carMakeCounts[make] = (carMakeCounts[make] || 0) + 1;
  carTypeCounts[type] = (carTypeCounts[type] || 0) + 1;
  carFuelCounts[fuel] = (carFuelCounts[fuel] || 0) + 1;
  carConditionCounts[condition] = (carConditionCounts[condition] || 0) + 1;

  if (!carModelsByMake[make]) {
    carModelsByMake[make] = new Set();
  }
  carModelsByMake[make].add(model);

});

// ================================
// Generate Dropdown Options
// ================================

// Generate car make options dynamically
export const carMakes = [
  { value: '', label: 'Car Make' },
  ...Object.keys(carMakeCounts).map((make) => ({
    value: make,
    label: make.charAt(0).toUpperCase() + make.slice(1),
  })),
];

// Generate car type options dynamically
export const carTypes = [
  { value: '', label: 'Car Type' },
  ...Object.keys(carTypeCounts).map((type) => ({
    value: type,
    label: type.charAt(0).toUpperCase() + type.slice(1),
  })),
];

// Generate car year options dynamically (static range for simplicity)
export const carYears = [
  { value: '', label: 'Select Year' },
  ...Array.from({ length: 36 }, (_, i) => {
    const year = new Date().getFullYear() - i;
    return { value: year.toString(), label: year.toString() };
  }),
];

// Generate car condition options dynamically
export const carConditions = [
  { value: '', label: 'Condition' },
  ...Object.keys(carConditionCounts).map((condition) => ({
    value: condition,
    label: condition.charAt(0).toUpperCase() + condition.slice(1),
  })),
];

// Generate fuel type options dynamically
export const carFuelTypes = [
  { value: '', label: 'Fuel Type' },
  ...Object.keys(carFuelCounts).map((fuel) => ({
    value: fuel,
    label: fuel.charAt(0).toUpperCase() + fuel.slice(1),
  })),
];

// ================================
// Utility Functions
// ================================

/**
 * Function to get car models based on selected make.
 * Returns an array of model options for the dropdown.
 */
export const getCarModels = (carMake) => {
  return carMake && carModelsByMake[carMake]
    ? [
        { value: '', label: 'Car Model' },
        ...Array.from(carModelsByMake[carMake]).map((model) => ({
          value: model,
          label: model.charAt(0).toUpperCase() + model.slice(1),
        })),
      ]
    : [{ value: '', label: 'Select a Make First', isDisabled: true }];
};

/**
 * Function to extract search parameters from the URL.
 * Returns an object with filtered search criteria.
 */
export const extractSearchParams = (searchParams) => {
  return {
    carMake: searchParams.get('carMake') || '',
    carModel: searchParams.get('carModel') || '',
    carType: searchParams.get('carType') || '',
    carYear: searchParams.get('carYear') || '',
    carCondition: searchParams.get('carCondition') || '',
    carFuelType: searchParams.get('carFuelType') || '',
    maxPrice: searchParams.get('maxPrice') || '',
  };
};

export const handleSelectChange = (setter) => (selectedOption) => {
  setter(selectedOption ? selectedOption.value : '');
};

export const resetFormFields = (setters) => {
  setters.forEach((setter) => setter(''));
};